import { IUser } from "@features/users/types";
import { baseApi } from "@store/utils";
import { IApiResponse } from "@app-types/response.types";
import { ILogin, IRegister } from "../types/auth.types";
import { authTokenChange, logoutUser, setUser } from "../slices/auth.slice";
import { AUTH_TAGS } from "../constants";


// Injecting Auth API Endpoints
export const authApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({

        getMe: builder.query<IUser, void>({
            query: () => ({
                url: "admin/auth/me",
                method: "GET",
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data) {
                        dispatch(setUser(data));
                    }
                } catch (error) {
                    console.error("Set user failed", error);
                }
            },
            providesTags: [AUTH_TAGS.GET_ME],
        }),
        // Registration
        registration: builder.mutation<IApiResponse<IUser>, IRegister>({
            query: (body) => ({
                url: "admin/auth/register",
                method: "POST",
                body,
            }),
        }),

        // Login
        login: builder.mutation({
            query: (body: ILogin) => ({
                url: "admin/auth/login",
                method: "POST",
                body,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data?.user) {
                        dispatch(setUser(data.user));
                        dispatch(authTokenChange({ accessToken: data.user.accessToken }));
                    }
                } catch (error) {
                    console.error("Login failed:", error);
                }
            },
        }),

        // Email Verification
        emailVerification: builder.mutation({
            query: (body: {
                email: string;
                otp: string;
                firstName: string;
                lastName: string;
            }) => ({
                url: "admin/auth/verify-otp",
                method: "POST",
                body,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(setUser(null));
                } catch (error) {
                    console.error("Email verification failed:", error);
                }
            },
        }),

        // Resend OTP
        resendOTP: builder.mutation({
            query: (body: { email: string }) => ({
                url: "admin/auth/resend-otp",
                method: "POST",
                body,
            }),
        }),

        // Forgot Password
        forgotPassword: builder.mutation({
            query: (body: { email: string }) => ({
                url: "admin/auth/forgot-password",
                method: "POST",
                body,
            }),
        }),

        // Reset Password
        resetPassword: builder.mutation({
            query: (body: { newPassword: string; token: string }) => ({
                url: "admin/auth/reset-password",
                method: "POST",
                body,
            }),
        }),

        // Logout
        logout: builder.mutation<any, { deviceId: string }>({
            query: ({ deviceId }) => ({
                url: `admin/auth/logout/${deviceId}`,
                method: "POST",
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(logoutUser());
                } catch (error) {
                    console.error("Logout failed:", error);
                }
            },
        }),

        // change password
        changePassword: builder.mutation<
            { message: string },
            { oldPassword: string; newPassword: string }
        >({
            query: (body) => ({
                url: "admin/auth/change-password",
                method: "POST",
                body,
            }),
        }),
    }),
});

// Export Hooks
export const {
    useGetMeQuery,
    useLoginMutation,
    useLogoutMutation,
    useRegistrationMutation,
    useEmailVerificationMutation,
    useResendOTPMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useChangePasswordMutation
} = authApi;
